import type PicklistsService from '@efdigital/ll-addon-content/services/picklists';
import {
  guessPresetScores,
  type Preset,
} from '@efdigital/ll-admin-v2/utils/quiz-choices';
import { type OptionWithValue } from '@efdigital/ll-admin-v2/utils/quiz-choices';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BsModal from 'ember-bootstrap/components/bs-modal';
import { cell, resource, use } from 'ember-resources';

export interface OptionsPresetsSignature {
  Args: {
    updateOptions: (options: OptionWithValue[]) => void;
  };
  Element: HTMLButtonElement;
}

function eq(a: unknown, b: unknown) {
  return a === b;
}

export class OptionsPresets extends Component<OptionsPresetsSignature> {
  @service declare picklists: PicklistsService;

  @tracked modalOpen = false;
  @tracked selectedPreset: Preset | null = null;

  @use presets = resource<Preset[]>(() => {
    const presets = cell<Preset[]>([]);

    const entries = Object.entries(this.picklists.groups).filter(
      ([_key, entry]) => entry.group.uses?.quizChoices === 'single'
    );
    presets.set(
      entries
        .map(([key, entry]) => ({
          key,
          name: entry.group.title,
          options: entry.items.map((item) => ({
            value: item.title,
            score: 0,
          })),
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    );

    return () => presets.current;
  });

  selectPreset = (preset: Preset) => {
    this.selectedPreset = preset;
  };

  applyPreset = () => {
    const preset = this.selectedPresetWithScores;
    if (preset) {
      this.args.updateOptions(
        preset.options.map((option) => ({
          value: option.value,
          score: option.score,
        }))
      );
      this.modalOpen = false;
    }
  };

  openModal = () => {
    this.modalOpen = true;
    this.selectedPreset = null;
  };

  closeModal = () => {
    this.modalOpen = false;
  };

  get selectedPresetWithScores(): (Preset & { hasScores: boolean }) | null {
    if (!this.selectedPreset) {
      return null;
    }

    const scoredOptions = guessPresetScores(this.selectedPreset);

    return {
      ...this.selectedPreset,
      options: scoredOptions || this.selectedPreset.options,
      hasScores: !!scoredOptions,
    };
  }

  <template>
    {{#if this.presets.length}}
      <button
        type="button"
        class="btn btn-white btn-sm py-1 btn-magical-border"
        aria-label="Presets"
        {{on "click" this.openModal}}
        ...attributes
      >
        <iconify-icon inline icon="ph:sparkle" />
        <span>Use Preset...</span>
      </button>
      <BsModal
        @open={{this.modalOpen}}
        @onHidden={{this.closeModal}}
        @size="lg"
        as |modal|
      >
        <modal.header>
          <h5 class="modal-title">Presets</h5>
        </modal.header>
        <modal.body>
          <div class="row">
            <div class="col-md-6">
              {{#each this.presets as |preset|}}
                <div class="form-check">
                  <input
                    type="radio"
                    class="form-check-input"
                    name="preset"
                    id={{preset.key}}
                    checked={{eq preset.key this.selectedPreset?.key}}
                    {{on "change" (fn this.selectPreset preset)}}
                  />
                  <label
                    class="form-check-label"
                    for={{preset.key}}
                  >{{preset.name}}</label>
                </div>
              {{/each}}
            </div>
            <div class="col-md-6">
              {{#let
                this.selectedPresetWithScores
                as |selectedPresetWithScores|
              }}
                {{#if selectedPresetWithScores}}
                  <h3 class="gud-eyebrow">{{selectedPresetWithScores.name}}</h3>
                  {{#if selectedPresetWithScores.hasScores}}
                    <table
                      class="table table-striped table-bordered table-hover table-condensed"
                    >
                      <thead>
                        <tr>
                          <th>Option</th>
                          <th>Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {{#each selectedPresetWithScores.options as |option|}}
                          <tr>
                            <td>{{option.value}}</td>
                            <td>{{option.score}}</td>
                          </tr>
                        {{/each}}
                      </tbody>
                    </table>
                  {{else}}
                    <ul>
                      {{#each selectedPresetWithScores.options as |option|}}
                        <li>{{option.value}} ({{option.score}})</li>
                      {{/each}}
                    </ul>
                  {{/if}}
                {{else}}
                  <p>Please select a preset to see the options.</p>
                {{/if}}
              {{/let}}
            </div>
          </div>
        </modal.body>
        <modal.footer>
          <div class="d-flex justify-content-center gap-2">
            <button
              type="button"
              class="btn btn-outline-primary"
              {{on "click" this.closeModal}}
            >Cancel</button>
            <button
              type="button"
              class="btn btn-primary"
              {{on "click" this.applyPreset}}
            >Apply</button>
          </div>
        </modal.footer>
      </BsModal>
    {{/if}}
  </template>
}
